import React, { useState, useContext, useEffect, useRef } from 'react'
import { Card, CardHeader, CardContent, Typography, Box, Avatar, IconButton } from '@mui/material'
import { EmpresasContext } from '../../../../contexts/EmpresasContext'
import AlertDescontoProgressivo from './AlertDescontoProgressivo'
import { AuthContext } from '../../../../contexts/AuthContext'
import HelpRoundedIcon from '@mui/icons-material/HelpRounded'
import { getSportNames } from '../../../../utils/functions'
import { LegendaStatusHorario } from '../../../Modais'
import { ScheduleDaySlider } from '../../../Complex'
import GradeHorarios from './GradeHorarios'
import { semFoto } from '../../../../assets'
import { SearchAlert } from '../../../Simple'
import moment from 'moment'
import { ToastMessages } from '../../../ToastMessages' // ← importe aqui

export default function QuadrasList() {
  const { sttAuthCtx } = useContext(AuthContext)
  const { sttPg, actionsCtxEmps, getCurrentDay } = useContext(EmpresasContext)

  async function buscarHorarios() {
    await actionsCtxEmps.BuscarGradeHorarios()
  }

  useEffect(() => {
    if (sttPg.modePg === 'quadras') buscarHorarios()
  }, [sttPg.selectedDay])

  const [mostrarLegenda, setMostrarLegenda] = useState(false)
  function switchMostrarLegenda() {
    setMostrarLegenda(prev => !prev)
  }

  // Ref para guardar o último GMT que já mostramos
  const lastGmtShown = useRef(undefined)
  // Ref para bloquear repetição de toast de fuso no mesmo ciclo
  const toastInThisCycle = useRef(false)
  // Ref para bloquear repetição do aviso de horário errado
  const hasShownTimeError = useRef(false)

  // Função que verifica se a empresa tem GMT diferente do usuário
  function VerificaGmtEmpresa() {
    const gmtEmpresa = sttPg.empresa?.gmt
    const gmtUsuario = moment().utcOffset() / 60 // GMT do usuário em horas
    return gmtEmpresa !== gmtUsuario
  }

  // useEffect que dispara toasts para:
  // 1) sttPg.validData.isValid === false (horário do dispositivo errado)
  // 2) mudança de sttPg.empresa.gmt com mismatch de fuso
  useEffect(() => {
    // Resetar bloqueio de toast de fuso a cada vez que entramos no efeito
    toastInThisCycle.current = false

    // 1) Verifica primeiro se o horário do dispositivo está errado:
    if (
      sttPg.validData &&
      sttPg.validData.isValid === false &&
      hasShownTimeError.current === false
    ) {
      // Dispara o toast apenas uma vez
      hasShownTimeError.current = true
      ToastMessages.persistentWarning(
        'Atenção: O horário de seu dispositivo parece estar configurado de maneira incorreta, utilize as configurações automáticas para evitar transtornos.',
      )
      // Não retornamos aqui pois ainda podemos precisar avisar sobre GMT
    }

    // 2) Agora verifica mismatch de GMT da empresa
    if (!sttPg.empresa || sttPg.empresa.gmt == null) return

    const gmtAtual = sttPg.empresa.gmt

    // Só dispara se:
    // a) gmtAtual mudou desde a última vez que mostramos
    // b) realmente há mismatch (VerificaGmtEmpresa() = true)
    // c) ainda não mostramos neste ciclo (toastInThisCycle.current === false)
    if (
      gmtAtual !== lastGmtShown.current &&
      VerificaGmtEmpresa() &&
      toastInThisCycle.current === false
    ) {
      toastInThisCycle.current = true
      lastGmtShown.current = gmtAtual

      ToastMessages.persistentWarning(
        `Atenção: O fuso horário de seu dispositivo difere do fuso deste centro esportivo (${gmtAtual === -3 ? 'Horário de Brasília' : `GMT${gmtAtual}`}).`,
      )
    }
  }, [sttPg.empresa?.gmt, sttPg.validData?.isValid])

  function VerificaDesativarGrade(quadra) {
    // a) Desativa se houver alguma desativaGrades para o dia
    if (quadra.desativaGrades?.length) {
      for (let item of quadra.desativaGrades) {
        const selectedDay = moment(sttPg.selectedDay).startOf('day')
        const dataInicio = moment(item.dataInicio).startOf('day')
        const dataFim = moment(item.dataFim).startOf('day')
        if (selectedDay.isBetween(dataInicio, dataFim, undefined, '[]')) {
          return true
        }
      }
    }

    // b) Verifica limite máximo de dias para agendar (desktop)
    if (!sttAuthCtx.isMobile && quadra.regraAgendamento.maximoTempoAgendamento > 0) {
      const selectedDay = moment(sttPg.selectedDay).startOf('day')
      const today = getCurrentDay().startOf('day')
      const diff = selectedDay.diff(today, 'days')
      if (diff >= quadra.regraAgendamento.maximoTempoAgendamento) {
        return true
      }
    }

    return false
  }

  return (
    <>
      {mostrarLegenda && (
        <LegendaStatusHorario onClose={switchMostrarLegenda} open={mostrarLegenda} />
      )}
      <CardHeader
        avatar={<div className='tw-w-4px' />}
        title={
          <Typography variant='h5' align='center'>
            Agende seu Horário
          </Typography>
        }
        action={
          <IconButton onClick={switchMostrarLegenda}>
            <HelpRoundedIcon color='primary' />
          </IconButton>
        }
      />
      <Box className='tw-mb-4'>
        <ScheduleDaySlider />
      </Box>

      {sttPg.empresa?.quadras?.length ? (
        sttPg.empresa.quadras.map((quadra, index) => (
          <React.Fragment key={index}>
            <Card style={{ marginBottom: '16px' }} raised>
              <CardHeader
                sx={stDesk.containCardHeader}
                avatar={
                  <Avatar
                    src={quadra.urlFoto + quadra.foto}
                    alt={quadra.descricao}
                    variant='rounded'
                    style={{ width: 150, height: 100 }}
                    onError={e => {
                      e.target.onerror = null // Evita loop infinito
                      e.target.src = semFoto // Define imagem padrão se falhar
                    }}
                  />
                }
                title={
                  <Box sx={stDesk.containHeader}>
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      <Typography variant='h6'>{quadra.descricao}</Typography>
                      <Typography variant='body1' sx={{ color: '#ff7043', fontWeight: 'bold' }}>
                        {getSportNames(quadra.categoriasEsporte)}
                      </Typography>
                      <Typography variant='body2'>{quadra.endereco}</Typography>
                    </Box>

                    {quadra.grades?.some(grad => grad.descontoProgressivo !== null) &&
                      !VerificaDesativarGrade(quadra) && (
                        <Box sx={stDesk.containDescProgress}>
                          <AlertDescontoProgressivo idquadra={quadra.idquadra} />
                        </Box>
                      )}
                  </Box>
                }
              />
              <CardContent sx={stDesk.cardContent}>
                <GradeHorarios
                  quadra={quadra}
                  horarios={quadra.horarios || []}
                  desativarGrade={VerificaDesativarGrade(quadra)}
                />
              </CardContent>
            </Card>
          </React.Fragment>
        ))
      ) : (
        <SearchAlert text='No momento esse centro esportivo não oferece espaços para agendamento.' />
      )}
    </>
  )
}

const stDesk = {
  containCardHeader: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    pb: 0,
    height: 'auto',
  },
  containHeader: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    width: '100%',
    height: 'auto',
  },
  containDescProgress: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '5px 5px 5px 10%',
    minWidth: '450px',
    width: 'auto',
    height: 'auto',
    borderRadius: '0px 15px 15px 0px',
    background: 'linear-gradient(to right, transparent 10%, #11bf68 25%, #11bf68 100%)',
  },
  cardContent: {
    pt: 1,
  },
}
